import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { trackPurchase } from "../lib/analytics";

export default function BookingSuccess() {
  const { lang } = useLang();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", payment_status: "", amount_total: 0 });
  const trackedRef = useRef(false);

  useEffect(() => {
    if (!sessionId) return;
    let attempts = 0;
    const poll = async () => {
      if (attempts >= 8) {
        setState((s) => ({ ...s, status: "timeout" }));
        return;
      }
      try {
        const r = await api.get(`/checkout/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          setState({ ...r.data, status: "paid" });
          // Fire purchase event exactly once per success view
          if (!trackedRef.current) {
            trackedRef.current = true;
            const b = r.data.booking;
            trackPurchase({
              booking: {
                id: b?.id || sessionId,
                apartment_id: b?.apartment_id || "unknown",
                nights: b?.nights || 1,
                total_price: b?.total_price ?? r.data.amount_total / 100,
                currency: (r.data.currency || "eur").toUpperCase(),
              },
            });
          }
          return;
        }
        if (r.data.status === "expired") {
          setState({ ...r.data, status: "expired" });
          return;
        }
        attempts += 1;
        setTimeout(poll, 2000);
      } catch (e) {
        attempts += 1;
        setTimeout(poll, 2000);
      }
    };
    poll();
  }, [sessionId]);

  const isEl = lang === "el";
  return (
    <div className="min-h-screen bg-[#F5F2EB] flex items-center justify-center px-6">
      <div
        data-testid="booking-success-card"
        className="max-w-xl w-full bg-white rounded-3xl p-10 sm:p-14 border border-[#E9E2D3] shadow-sm text-center"
      >
        {state.status === "paid" ? (
          <>
            <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-6" />
            <h1 className="font-serif text-4xl text-[#1A2B4C] mb-3">
              {isEl ? "Η κράτησή σας επιβεβαιώθηκε!" : "Booking confirmed!"}
            </h1>
            <p className="text-gray-600 mb-2">
              {isEl
                ? "Σας ευχαριστούμε. Θα λάβετε σύντομα email επιβεβαίωσης."
                : "Thank you. You'll receive a confirmation email shortly."}
            </p>
            <p className="font-serif text-2xl text-[#1A2B4C] mt-6">
              €{(state.amount_total / 100).toFixed(2)}
            </p>
          </>
        ) : state.status === "polling" ? (
          <>
            <Loader2 className="w-12 h-12 text-[#1A2B4C] mx-auto mb-6 animate-spin" />
            <h1 className="font-serif text-3xl text-[#1A2B4C] mb-2">
              {isEl ? "Επαλήθευση πληρωμής..." : "Verifying payment..."}
            </h1>
            <p className="text-gray-600">
              {isEl ? "Παρακαλώ περιμένετε λίγο." : "Please wait a moment."}
            </p>
          </>
        ) : (
          <>
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
            <h1 className="font-serif text-3xl text-[#1A2B4C] mb-3">
              {isEl ? "Κάτι πήγε στραβά" : "Something went wrong"}
            </h1>
            <p className="text-gray-600">
              {isEl
                ? "Επικοινωνήστε μαζί μας εάν χρεωθήκατε."
                : "Please contact us if you were charged."}
            </p>
          </>
        )}
        <button
          onClick={() => navigate("/")}
          data-testid="success-home-btn"
          className="mt-8 bg-[#1A2B4C] text-white px-8 py-3 rounded-full text-sm uppercase tracking-widest hover:-translate-y-0.5 transition-transform"
        >
          {isEl ? "Επιστροφή" : "Back to Home"}
        </button>
      </div>
    </div>
  );
}
